"use client";

import dynamic from "next/dynamic";
import { useEffect, useState, useSyncExternalStore } from "react";
import { hasSignedInClerkClient } from "@/lib/clerk-session-signal";
import {
  isClerkScopePresent,
  subscribeToClerkProviders,
} from "@/components/auth/clerk-scope-presence";

// The only path from a Clerk-free surface to Clerk code, and a dynamic one:
// the chunk (the Clerk SDK, then clerk-js itself) is requested only for a
// signed-in visitor, after hydration.
const SignedInClerkProvider = dynamic(
  () =>
    import("@/components/auth/signed-in-clerk-provider").then((module) => ({
      default: module.SignedInClerkProvider,
    })),
  { ssr: false },
);

// Server and hydration render: "a scope is present", so nothing Clerk-related
// is ever server-rendered or part of the hydration pass.
const serverScopePresent = () => true;

/**
 * Root-layout keeper of a signed-in visitor's Clerk session on surfaces that
 * ship no Clerk (draft 8 §1, Alec's 26 Oct decision): marketing, ads, premium
 * ads and the token pages. Anonymous visitors — no signed-in `__client_uat`
 * cookie — load nothing. A signed-in visitor gets `SignedInClerkProvider`
 * (clerk-js keeps `__session` fresh; the Account consent bridge runs), unless
 * a route group's own `ClerkScope` is mounted. Clerk throws on a second
 * mounted provider, so the scope and this upkeep hand over through
 * components/auth/clerk-scope-presence.tsx and never overlap.
 *
 * The cookie is re-read on focus/visibility, so signing in or out in another
 * tab is picked up when the visitor returns.
 */
export function SignedInClerkUpkeep() {
  const scopePresent = useSyncExternalStore(
    subscribeToClerkProviders,
    isClerkScopePresent,
    serverScopePresent,
  );
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    const check = () => setSignedIn(hasSignedInClerkClient(document.cookie));
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    check();
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  if (scopePresent || !signedIn) return null;
  return <SignedInClerkProvider />;
}
